<template>
  <div class="map-page-wrapper">
    <div v-if="isLoading" class="loading-overlay">
      <h1><i class="fas fa-spinner fa-spin"></i> Loading Hero Map...</h1>
    </div>

    <ProgressionMap
      v-else-if="progression"
      :progressionData="progression"
      :playerRace="gameStore.raceLabelKey"
      :socketArtByNode="gameStore.socketArtByNodeKey"
      @node-selected="handleNodeSelect"
    />

    <div v-else class="loading-overlay">
      <h1><i class="fas fa-exclamation-triangle"></i> Error loading map.</h1>
    </div>
    <NodeModal
      :isVisible="isModalVisible"
      :nodeData="selectedNodeData"
      :playerRace="gameStore.raceLabelKey"
      :isLoading="isUnlocking"
      :errorMessage="modalErrorMessage"
      :enableCardSocketing="true"
      @close="isModalVisible = false"
      @confirm-unlock="handleConfirmUnlock"
      @socket-card="handleSocketCard"
      @unsocket-card="handleUnsocketCard"
    />
  </div>
</template>

<script setup>
import { onMounted, computed, ref } from "vue";
import { useGameStore } from "../store/game";
import ProgressionMap from "../components/ProgressionMap.vue";
import NodeModal from "../components/NodeModal.vue";

const gameStore = useGameStore();

// Get data from the store
const progression = computed(() => gameStore.heroProgression);
const isLoading = computed(() => gameStore.isLoading);
const isModalVisible = ref(false);
const selectedNodeData = ref(null);
const isUnlocking = ref(false);
const modalErrorMessage = ref("");

// Keep track of the selected node
const selectedNode = ref(null);

// When the component mounts, tell the store to fetch the data
onMounted(async () => {
  if (!gameStore.heroProgression) {
    await gameStore.fetchHeroProgression();
  }
  if (gameStore.activeHero) {
    await Promise.all([
      gameStore.fetchPlayerCards(),
      gameStore.fetchHeroSockets(gameStore.activeHero.id),
    ]);
  }
});

// This function will be used to show the floating panel
const handleNodeSelect = (data) => {
  // 'data' is { node, status } or null
  modalErrorMessage.value = ""; // Clear old errors
  if (data && data.node) {
    selectedNodeData.value = data;
    isModalVisible.value = true;
  } else {
    selectedNodeData.value = null;
    isModalVisible.value = false;
  }
};

const handleConfirmUnlock = async (nodeKey, extra = {}) => {
  if (!nodeKey) return;
  modalErrorMessage.value = "";
  isUnlocking.value = true;

  try {
    if (!gameStore.activeHero) {
      if (nodeKey !== "Hero") {
        modalErrorMessage.value = "Activate the Hero root node first.";
        return;
      }
      await gameStore.activateHero(nodeKey, extra.heroName);
      await Promise.all([
        gameStore.fetchPlayerCards(),
        gameStore.fetchHeroSockets(gameStore.activeHero.id),
      ]);
    } else {
      await gameStore.unlockHeroNode(nodeKey);
    }
    // Success handling (SAME)
    isModalVisible.value = false;
    selectedNodeData.value = null;
  } catch (error) {
    modalErrorMessage.value = error.message;
  } finally {
    isUnlocking.value = false;
  }
};

const handleSocketCard = async ({ nodeKey, playerCardId }) => {
  if (!nodeKey || !playerCardId || !gameStore.activeHero) return;
  modalErrorMessage.value = "";
  isUnlocking.value = true;
  try {
    await gameStore.socketHeroCard(
      gameStore.activeHero.id,
      nodeKey,
      playerCardId
    );
  } catch (error) {
    modalErrorMessage.value =
      error.response?.data?.message || error.message || "Failed to socket card.";
  } finally {
    isUnlocking.value = false;
  }
};

const handleUnsocketCard = async ({ nodeKey }) => {
  if (!nodeKey || !gameStore.activeHero) return;
  modalErrorMessage.value = "";
  isUnlocking.value = true;
  try {
    await gameStore.unsocketHeroCard(gameStore.activeHero.id, nodeKey);
  } catch (error) {
    modalErrorMessage.value =
      error.response?.data?.message ||
      error.message ||
      "Failed to unsocket card.";
  } finally {
    isUnlocking.value = false;
  }
};
</script>

<style scoped>
.map-page-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
  background: transparent;
}

.loading-overlay {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #00f6ff;
  font-family: "Orbitron", sans-serif;
  /* Add a backdrop so text is readable on the image */
  background-color: rgba(10, 10, 20, 0.7);
  backdrop-filter: blur(5px);
}
</style>
